import { InputType } from 'config/input.type.constants'
import { FormTableHeader } from 'stories/components'

export const header: FormTableHeader[] = [
  { key: 'liability', title: 'Liability', sortable: true },
  { key: 'balance', title: 'Unpaid Balance', sortable: true },
  { key: 'monthlyPayment', title: 'Monthly Payment', sortable: true },
  { key: 'accountType', title: 'Account Type', sortable: true },
  { key: 'toBePaid', title: 'To Be Paid Off', sortable: true },
]

const accoutTypes = [
  'Revolving',
  'Installment',
  'Mortgage',
  'HELOC',
  'Collections Judgements and Liens',
  'Lease Payments',
  'Open 30-Day',
  'Taxes',
  'Tax Lien',
  'Other',
]

const priorityTypes = ['First Lien', 'Second Lien', 'Third Lien']

const subjectPropertyAccountTypes = ['Mortgage', 'HELOC']

export const defaultInputs = (
  borrowerOptions: Record<string, any>,
  realEstateOptions: Record<string, any>,
): Record<string, InputType> => {
  return {
    liability: {
      inputType: 'text',
      title: 'Liability',
      required: true,
    },
    borrower: {
      inputType: 'select',
      options: borrowerOptions,
      title: 'Borrower',
      hasDefaultOption: true,
      required: true,
    },
    accountType: {
      inputType: 'select',
      options: accoutTypes,
      hasDefaultOption: true,
      title: 'Account Type',
    },
    accountTypeOther: {
      inputType: 'text',
      title: 'Account Type Other',
      visible: (inputs: Record<string, InputType>) => inputs.accountType.value == 'Other',
    },
    propertyId: {
      inputType: 'select',
      options: realEstateOptions,
      title: 'Real Estate Owned',
      visible: (inputs: Record<string, InputType>) => subjectPropertyAccountTypes.includes(inputs.accountType.value),
      hasDefaultOption: true,
    },
    mortgageType: {
      inputType: 'select',
      options: ['VA', 'FHA', 'Conventional', 'USDA-RD', 'Other'],
      title: 'Mortgage Type',
      visible: (inputs: Record<string, InputType>) => subjectPropertyAccountTypes.includes(inputs.accountType.value),
      hasDefaultOption: true,
    },
    attn: {
      inputType: 'text',
      title: 'Attn',
    },
    address: {
      inputType: 'map',
      title: 'Address',
    },
    accountNo: {
      inputType: 'text',
      title: 'Account No',
    },
    paymentsLeft: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Payments Left',
    },
    balance: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Unpaid Balance',
      required: true,
      prefix: '$',
    },
    creditLimit: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Credit Limit',
      required: false,
      prefix: '$',
      visible: (inputs: Record<string, InputType>) => ['Revolving', 'HELOC'].includes(inputs.accountType.value),
    },
    monthlyPayment: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Monthly Payment',
      prefix: '$',
    },
    priority: {
      inputType: 'select',
      title: 'Priority',
      options: priorityTypes,
      hasDefaultOption: true,
      visible: (inputs: Record<string, InputType>) => subjectPropertyAccountTypes.includes(inputs.accountType.value),
    },
    toBePaid: {
      inputType: 'check',
      title: 'To Be Paid Off?',
      required: true,
    },
    resubordinated: {
      inputType: 'check',
      title: 'Resubordinated',
    },
    omitted: {
      inputType: 'check',
      title: 'Omitted',
    },
    listedOnCR: {
      inputType: 'check',
      title: 'Listed on Credit Report',
    },
    isSubjectProperty: {
      inputType: 'check',
      title: 'Is Subject Property?',
      visible: (inputs: Record<string, InputType>) => subjectPropertyAccountTypes.includes(inputs.accountType.value),
      required: true,
    },
    email: {
      inputType: 'text',
      type: 'email',
      title: 'Email',
      required: false,
    },
    phone: {
      inputType: 'text',
      type: 'phone',
      title: 'Phone',
      required: false,
    },
    fax: {
      inputType: 'text',
      type: 'text',
      title: 'Fax',
      required: false,
    },
    notCounted: {
      inputType: 'checkbox',
      title: 'Not Counted',
      required: false,
    },
    QMATRNotes: {
      inputType: 'textarea',
      title: 'ATR Notes',
      required: false,
    },
    otherDesc: {
      inputType: 'textarea',
      title: 'Other Notes',
      required: false,
    },
  }
}

const expenseTypeOptions = {
  0: 'Not Assigned',
  1: 'Alimony',
  2: 'Child Support',
  3: 'Separate Maintenance',
  4: 'Job Related Expenses',
  5: 'Other',
}

const accountHeldByOptions = {
  0: 'Not Assigned',
  1: 'Jointly',
  2: 'Individually',
}

export const expenseInputs = (): Record<string, InputType> => {
  return {
    accountHeldByType: {
      inputType: 'select',
      title: 'Account Held By',
      options: accountHeldByOptions,
      hasDefaultOption: true,
    },
    expenseType: {
      inputType: 'select',
      title: 'Type of Expense',
      options: expenseTypeOptions,
      hasDefaultOption: true,
    },
    otherDesc: {
      inputType: 'text',
      title: 'Other Expense Description',
      maxLength: 80,
      required: false,
      visible: (inputs: Record<string, InputType>) => inputs.expenseType?.value == '5',
    },
    moPayment: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Monthly Payment',
      prefix: '$',
    },
    paymentsLeft: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Payments Left',
    },
    QMATRNotes: {
      inputType: 'textarea',
      title: 'QM ATR Notes',
      maxLength: 500,
      rows: 5,
      span: 'full',
    },
    notes: {
      inputType: 'textarea',
      title: 'Notes',
      maxLength: 500,
      rows: 5,
      span: 'full',
    },
  }
}
